import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { getDocumentHelper } from '../../../../actions/utility-actions';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { PagedList } from '../../../../components/paged-list/PagedList';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { useScrollMemory } from '../../../../hooks/use-scroll-memory';
import { useSearchParams } from '../../../../hooks/use-search-params';
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes, ScrollStatePaths } from '../../../../utils/app-constants';
import { navigateToPage } from '../../../../utils/history';
import * as transportActions from '../../../../transportation/actions';
import { PickupActionEditInfo } from '../components/PickupActionEditInfo';
import { PickupRow } from '../components/PickupRow';
import { PickupSearchFilters } from '../../../../transportation/models/filters/pickup-search-filters';
import { LoadSheetDocument } from '../../../../transportation/models/pickups/load-sheet-document';
import { PickupRead } from '../../../../transportation/models/pickups/pickup-read';
import { PickupSummary } from '../../../../transportation/models/pickups/pickup-summary';
import { getPickupSearchFilters } from '../../../../transportation/selectors';
import { TransportationSvc } from '../../../../transportation/service';
import '../../../../transportation/styles.scss';

export const PickupsScene = () => {

    const store = useStore();
    const dispatch = useDispatch();
    const [service] = React.useState(new TransportationSvc());
    const [showInfoFlyout, setShowInfoFlyout] = React.useState(false);
    const [editPickup, setEditPickup] = React.useState(new PickupRead());

    const [filterParams, setFilterParams] = React.useState(() => {
        return getPickupSearchFilters(store.getState());
    });

    const [pickups, setPickups] = React.useState(() => {
        const initial = new PagedApiResult<PickupSummary>();
        initial.pagination = filterParams.paginationInfo;
        initial.pagination.reusePaging = true;
        return initial;
    });

    const scrollTo = useScrollMemory(
        ScrollStatePaths.SHIPMENTS_PICKUPS,
        Routes.SHIPPING_PICKUP_EDIT
    );

    useSearchParams(
        Routes.SHIPPING_PICKUP_EDIT,
        transportActions.updatePickupSearchFilters(filterParams),
        transportActions.clearPickupSearch()
    );

    /**
     * OnSearch handler
     * @param selectedSearchIdx The index of the search param
     * @param params The search param object
     * @param pagingInfo The pagination info from the table
     */
    const onSearch = async (filters: PickupSearchFilters) => {
        const response = await service.search(PickupSummary, filters);
        if (response.status === StatusCode.Success) {
            setPickups(response.data);
            scrollTo();
        }

        setFilterParams(filters);
    };

    /**
     * Download the load sheet
     * @param dealerId The dealer ID
     * @param id The pickup ID
     */
    const downloadLoadsheet = async (dealerId: string, id: string) => {
        const req = new LoadSheetDocument(dealerId, id);
        dispatch(getDocumentHelper(req));
    };

    const onFlyoutSuccess = async (result: number) => {
        setEditPickup(new PickupRead());
        setShowInfoFlyout(false);

        // For now we will navigate to the newly created pickup.
        // Could be revisited later with a call to search
        // await onSearch(filterParams);
        navigateToPage(Routes.SHIPPING_PICKUP_EDIT_WITH_ID.formattify(result));

    };

    const onParamsUpdate = (itm: any) => {
        setFilterParams(itm);
    };

    const onParamsReset = () => {
        const tmp = new PickupSearchFilters();
        setFilterParams(tmp);
    };

    const onFlyoutClose = async () => {
        setShowInfoFlyout(false);
    };

    return (<PageContent classes="containers-list" pageName={PageNames.TRANSPORTATION_PICKUP_LIST}>
        <PageHeading>
            <HasPermission requiredPermissions={[
                Permissions.ShippingPickupsEdit
            ]}>
                <button className="btn new attention" onClick={() => setShowInfoFlyout(true)}>
                    Create Pickup
                </button>
            </HasPermission>
        </PageHeading>
        <PageBody>
            <PagedList
                noResultsMessage="No pickups found, please search again."
                searchCallback={onSearch}
                data={pickups}
                filterProps={filterParams}
                selectableClass=""
                onFilterUpdate={onParamsUpdate}
                reduxDispatch={dispatch}
                onReset={onParamsReset}
                rowRenderer={(pickup: PickupSummary) =>
                    <PickupRow
                        key={pickup.id}
                        pickup={pickup}
                        viewLoadsheet={downloadLoadsheet}
                    />
                }
            />

            <PickupActionEditInfo
                visible={showInfoFlyout}
                onSuccess={onFlyoutSuccess}
                onClose={onFlyoutClose}
                pickup={editPickup}
            />
        </PageBody>
    </PageContent>);
};
